import { useState, useEffect, useCallback, useMemo } from "react";
import API from "../lib/api";
import { useAuth } from "../contexts/AuthContext";

const ROLES = ["super_admin", "admin", "project_manager"];
const TTL = 60_000;
let cache = { at: 0, items: [] };
let inflight = null;
const listeners = new Set();

export function invalidateClientWaiting() {
  cache = { at: 0, items: cache.items };
  listeners.forEach((fn) => fn());
}

/* Open items whose latest client message has gone unanswered for over a day — cached for a minute across pages. */
export function useClientWaiting() {
  const { user } = useAuth();
  const enabled = ROLES.includes(user?.role);
  const [items, setItems] = useState(cache.items);

  const load = useCallback(async (force = false) => {
    if (!enabled) return;
    if (!force && Date.now() - cache.at < TTL) { setItems(cache.items); return; }
    inflight = inflight || API.get("/notes/client-waiting")
      .then(({ data }) => { cache = { at: Date.now(), items: data.items || [] }; return cache.items; })
      .catch(() => cache.items)
      .finally(() => { inflight = null; });
    setItems(await inflight);
  }, [enabled]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    const fn = () => load(true);
    listeners.add(fn);
    return () => listeners.delete(fn);
  }, [load]);

  const byId = useMemo(() => Object.fromEntries(items.map((i) => [i.id, i])), [items]);
  return { items, byId, refresh: () => load(true) };
}
